import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { getError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Printer, FileSpreadsheet, Plus, Pencil, Trash2, Eye, FileText,
  CheckCircle2, XCircle, Users, Loader2, FilterX, ImageOff,
} from "lucide-react";

const emptyForm = { decision_number: "", date: "", category_id: "", status_id: "", employee_id: "", details: "", image: "" };

export default function Decisions() {
  const { can } = useAuth();
  const [decisions, setDecisions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [entity, setEntity] = useState("");
  const [stats, setStats] = useState({ total: 0, active: 0, cancelled: 0, employees: 0 });
  const [search, setSearch] = useState("");
  const [fCat, setFCat] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [printItem, setPrintItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (fCat !== "all") params.category_id = fCat;
      if (fStatus !== "all") params.status_id = fStatus;
      const [d, s] = await Promise.all([api.get("/decisions", { params }), api.get("/decisions/stats")]);
      setDecisions(d.data);
      setStats(s.data);
    } catch (e) {
      toast.error(getError(e, "تعذر تحميل القرارات"));
    } finally {
      setLoading(false);
    }
  }, [search, fCat, fStatus]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [c, s, e, en] = await Promise.all([
          api.get("/categories"),
          api.get("/statuses"),
          api.get("/employees"),
          api.get("/entity"),
        ]);
        setCategories(c.data);
        setStatuses(s.data);
        setEmployees(e.data);
        setEntity(en.data.name || "");
      } catch {}
    })();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      decision_number: d.decision_number,
      date: d.date,
      category_id: d.category_id,
      status_id: d.status_id,
      employee_id: d.employee_id,
      details: d.details || "",
      image: d.image || "",
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/decisions/${editing.id}`, form);
        toast.success("تم تعديل القرار بنجاح");
      } else {
        await api.post("/decisions", form);
        toast.success("تمت إضافة القرار بنجاح");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`هل أنت متأكد من حذف القرار رقم ${d.decision_number}؟`)) return;
    try {
      await api.delete(`/decisions/${d.id}`);
      toast.success("تم حذف القرار");
      load();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const onImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("حجم الصورة كبير جداً (الحد الأقصى 4 ميغابايت)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/decisions/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "القرارات.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("تم تصدير ملف Excel");
    } catch (err) {
      toast.error(getError(err, "تعذر التصدير"));
    }
  };

  const buildPrintHtml = (items) => {
    const logo = `${window.location.origin}/logo.png`;
    const sheets = items
      .map(
        (d) => `
      <div class="sheet">
        <div class="head">
          <img src="${logo}" alt="سجل" />
          <div class="entity">${entity || ""}</div>
          <div class="sys">سِجِل للأرشفة الإلكترونية</div>
        </div>
        <div class="section-title">بيانات الموظف</div>
        <table class="info">
          <tr><th>اسم الموظف</th><td>${d.employee_name || ""}</td><th>الرقم الوظيفي</th><td>${d.employee_number || ""}</td></tr>
          <tr><th>المسمى الوظيفي</th><td>${d.employee_job_title || ""}</td><th>القسم/الإدارة</th><td>${d.employee_department || ""}</td></tr>
          <tr><th>رقم القرار</th><td>${d.decision_number}</td><th>التاريخ</th><td>${d.date}</td></tr>
          <tr><th>الفئة</th><td>${d.category_name}</td><th>الحالة</th><td>${d.status_name}</td></tr>
        </table>
        ${d.details ? `<div class="section-title">تفاصيل إضافية</div><p class="details">${d.details}</p>` : ""}
        ${d.image ? `<div class="section-title">صورة القرار</div><img class="doc" src="${d.image}" />` : ""}
      </div>`
      )
      .join('<div class="pagebreak"></div>');
    return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>طباعة القرارات</title>
      <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap" rel="stylesheet">
      <style>
        * { font-family: 'Tajawal', sans-serif; box-sizing: border-box; }
        body { margin: 0; padding: 24px; background: #fff; color: #0F3D3E; }
        .sheet { max-width: 800px; margin: 0 auto; }
        .head { text-align: center; border-bottom: 3px solid #0F3D3E; padding-bottom: 16px; margin-bottom: 24px; }
        .head img { height: 90px; }
        .entity { font-size: 22px; font-weight: 800; margin-top: 8px; }
        .sys { color: #2A9D8F; font-weight: 700; }
        .section-title { background: #0F3D3E; color: #fff; padding: 6px 14px; border-radius: 6px; font-weight: 700; margin: 18px 0 10px; }
        table.info { width: 100%; border-collapse: collapse; }
        table.info th, table.info td { border: 1px solid #cbd5e1; padding: 8px 12px; font-size: 14px; }
        table.info th { background: #F5F5F0; width: 16%; text-align: right; }
        img.doc { max-width: 100%; border: 1px solid #cbd5e1; border-radius: 6px; }
        .pagebreak { page-break-after: always; }
        .details { border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px 14px; font-size: 14px; }
      </style></head><body>${sheets}<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 500); };<\/script></body></html>`;
  };

  const doPrint = (items) => {
    if (!items.length) {
      toast.error("لا توجد قرارات للطباعة");
      return;
    }
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("يرجى السماح بالنوافذ المنبثقة للطباعة");
      return;
    }
    w.document.write(buildPrintHtml(items));
    w.document.close();
  };

  const clearFilters = () => {
    setSearch("");
    setFCat("all");
    setFStatus("all");
  };

  const statCards = [
    { label: "عدد القرارات", value: stats.total, icon: FileText, color: "bg-primary", testid: "stat-total" },
    { label: "القرارات النافذة", value: stats.active, icon: CheckCircle2, color: "bg-secondary", testid: "stat-active" },
    { label: "القرارات الملغاة", value: stats.cancelled, icon: XCircle, color: "bg-red-500", testid: "stat-cancelled" },
    { label: "عدد الموظفين", value: stats.employees, icon: Users, color: "bg-accent", testid: "stat-employees" },
  ];

  return (
    <div className="space-y-6" data-testid="decisions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-extrabold text-primary">القرارات</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => doPrint(decisions)} className="border-primary/30 text-primary hover:bg-primary hover:text-white transition-colors" data-testid="print-all-btn">
            <Printer className="h-4 w-4 ms-1" />
            طباعة الكل
          </Button>
          <Button variant="outline" onClick={exportExcel} className="border-secondary/40 text-secondary hover:bg-secondary hover:text-white transition-colors" data-testid="export-excel-btn">
            <FileSpreadsheet className="h-4 w-4 ms-1" />
            تصدير اكسل
          </Button>
          {can("add") && (
            <Button onClick={openAdd} className="bg-primary hover:bg-secondary transition-colors" data-testid="add-decision-btn">
              <Plus className="h-4 w-4 ms-1" />
              إضافة قرار
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(({ label, value, icon: Icon, color, testid }) => (
          <div key={testid} className="bg-white rounded-lg border border-border shadow-[0_4px_20px_-4px_rgba(15,61,62,0.08)] p-5 flex items-center gap-4 hover:-translate-y-0.5 transition-transform duration-200" data-testid={testid}>
            <div className={`${color} text-white rounded-lg p-3`}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <div className="text-3xl font-extrabold text-primary" data-testid={`${testid}-value`}>{value}</div>
              <div className="text-sm text-muted-foreground">{label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-border p-4 flex flex-col md:flex-row gap-3 md:items-center">
        <Input
          placeholder="بحث في بيانات القرارات..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:flex-1"
          data-testid="decisions-search-input"
        />
        <Select value={fCat} onValueChange={setFCat}>
          <SelectTrigger className="md:w-48" data-testid="filter-category-select">
            <SelectValue placeholder="الفئة" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الفئات</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="md:w-40" data-testid="filter-status-select">
            <SelectValue placeholder="الحالة" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الحالات</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground hover:text-primary" data-testid="clear-filters-btn">
          <FilterX className="h-4 w-4 ms-1" />
          إلغاء البحث والفلترة
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="decisions-table">
          <thead>
            <tr className="bg-primary text-white">
              <th className="px-3 py-3 text-right font-bold">م</th>
              <th className="px-3 py-3 text-right font-bold">رقم القرار</th>
              <th className="px-3 py-3 text-right font-bold">التاريخ</th>
              <th className="px-3 py-3 text-right font-bold">الفئة</th>
              <th className="px-3 py-3 text-right font-bold">الموظف</th>
              <th className="px-3 py-3 text-right font-bold">الحالة</th>
              <th className="px-3 py-3 text-right font-bold">رابط الصورة</th>
              <th className="px-3 py-3 text-right font-bold">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></td></tr>
            ) : decisions.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="decisions-empty">لا توجد قرارات مسجلة</td></tr>
            ) : (
              decisions.map((d, i) => (
                <tr key={d.id} className="border-t border-border hover:bg-muted/50 transition-colors" data-testid={`decision-row-${i}`}>
                  <td className="px-3 py-3">{i + 1}</td>
                  <td className="px-3 py-3 font-bold text-primary">{d.decision_number}</td>
                  <td className="px-3 py-3">{d.date}</td>
                  <td className="px-3 py-3">{d.category_name}</td>
                  <td className="px-3 py-3">{d.employee_name}</td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${d.status_name === "نافذ" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {d.status_name}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    {d.image ? (
                      <Button variant="ghost" size="sm" onClick={() => setImagePreview(d)} className="text-secondary" data-testid={`view-image-btn-${i}`}>
                        <Eye className="h-4 w-4 ms-1" />
                        عرض الصورة
                      </Button>
                    ) : (
                      <span className="text-muted-foreground text-xs inline-flex items-center gap-1"><ImageOff className="h-3 w-3" />لا توجد</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setPrintItem(d)} className="text-primary" title="طباعة" data-testid={`print-decision-btn-${i}`}>
                        <Printer className="h-4 w-4" />
                      </Button>
                      {can("edit") && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(d)} className="text-secondary" title="تعديل" data-testid={`edit-decision-btn-${i}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {can("delete") && (
                        <Button variant="ghost" size="sm" onClick={() => remove(d)} className="text-red-600" title="حذف" data-testid={`delete-decision-btn-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="decision-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-primary">{editing ? "تعديل قرار" : "إضافة قرار"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>رقم القرار</Label>
              <Input required value={form.decision_number} onChange={(e) => setForm({ ...form, decision_number: e.target.value })} data-testid="decision-number-input" />
            </div>
            <div className="space-y-2">
              <Label>تاريخ القرار</Label>
              <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="decision-date-input" />
            </div>
            <div className="space-y-2">
              <Label>الفئة</Label>
              <Select required value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="decision-category-select"><SelectValue placeholder="اختر الفئة" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>الحالة</Label>
              <Select required value={form.status_id} onValueChange={(v) => setForm({ ...form, status_id: v })}>
                <SelectTrigger data-testid="decision-status-select"><SelectValue placeholder="اختر الحالة" /></SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>الموظف</Label>
              <Select required value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="decision-employee-select"><SelectValue placeholder="اختر الموظف" /></SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name} — {emp.employee_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>تفاصيل إضافية</Label>
              <Textarea rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="decision-details-input" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>صورة القرار (إرفاق صورة)</Label>
              <Input type="file" accept="image/*" onChange={onImagePick} data-testid="decision-image-input" />
              {form.image && <img src={form.image} alt="معاينة" className="mt-2 max-h-40 rounded-md border border-border" data-testid="decision-image-preview" />}
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="submit" disabled={saving} className="bg-primary hover:bg-secondary transition-colors" data-testid="decision-save-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin ms-2" />}
                {editing ? "حفظ التعديلات" : "إضافة القرار"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(imagePreview)} onOpenChange={() => setImagePreview(null)}>
        <DialogContent className="sm:max-w-3xl" dir="rtl" data-testid="image-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-primary">صورة القرار رقم {imagePreview?.decision_number}</DialogTitle>
          </DialogHeader>
          {imagePreview?.image && <img src={imagePreview.image} alt="صورة القرار" className="w-full rounded-md border border-border" />}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(printItem)} onOpenChange={() => setPrintItem(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="print-dialog">
          <DialogHeader>
            <DialogTitle className="text-primary">معاينة طباعة القرار</DialogTitle>
          </DialogHeader>
          {printItem && (
            <div className="space-y-4">
              <div className="text-center border-b-4 border-primary pb-4">
                <img src="/logo.png" alt="سجل" className="h-20 mx-auto object-contain" />
                {entity && <div className="text-xl font-extrabold text-primary mt-2">{entity}</div>}
                <div className="text-secondary font-bold text-sm">سِجِل للأرشفة الإلكترونية</div>
              </div>
              <div className="bg-primary text-white rounded-md px-4 py-2 font-bold">بيانات الموظف</div>
              <table className="w-full text-sm border-collapse">
                <tbody>
                  <tr className="border border-border"><th className="bg-muted px-3 py-2 text-right w-40 border-e border-border">اسم الموظف</th><td className="px-3 py-2">{printItem.employee_name}</td></tr>
                  <tr className="border border-border"><th className="bg-muted px-3 py-2 text-right border-e border-border">الرقم الوظيفي</th><td className="px-3 py-2">{printItem.employee_number}</td></tr>
                  <tr className="border border-border"><th className="bg-muted px-3 py-2 text-right border-e border-border">المسمى الوظيفي</th><td className="px-3 py-2">{printItem.employee_job_title}</td></tr>
                  <tr className="border border-border"><th className="bg-muted px-3 py-2 text-right border-e border-border">القسم/الإدارة</th><td className="px-3 py-2">{printItem.employee_department}</td></tr>
                  <tr className="border border-border"><th className="bg-muted px-3 py-2 text-right border-e border-border">رقم القرار</th><td className="px-3 py-2 font-bold">{printItem.decision_number}</td></tr>
                  <tr className="border border-border"><th className="bg-muted px-3 py-2 text-right border-e border-border">التاريخ</th><td className="px-3 py-2">{printItem.date}</td></tr>
                  <tr className="border border-border"><th className="bg-muted px-3 py-2 text-right border-e border-border">الفئة / الحالة</th><td className="px-3 py-2">{printItem.category_name} — {printItem.status_name}</td></tr>
                </tbody>
              </table>
              {printItem.details && (
                <>
                  <div className="bg-primary text-white rounded-md px-4 py-2 font-bold">تفاصيل إضافية</div>
                  <p className="border border-border rounded-md px-4 py-3 text-sm">{printItem.details}</p>
                </>
              )}
              {printItem.image && (
                <>
                  <div className="bg-primary text-white rounded-md px-4 py-2 font-bold">صورة القرار</div>
                  <img src={printItem.image} alt="صورة القرار" className="w-full rounded-md border border-border" />
                </>
              )}
              <DialogFooter>
                <Button onClick={() => doPrint([printItem])} className="bg-primary hover:bg-secondary transition-colors" data-testid="print-confirm-btn">
                  <Printer className="h-4 w-4 ms-1" />
                  طباعة
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
